/**
 * ChatNode - Canvas node for conversational AI interaction
 *
 * Features:
 * - Collaborative message history via Yjs (all users see same chat)
 * - Streaming AI responses
 * - Pin button on AI messages to create BlockNodes
 * - Output port for streaming to connected BlockNode
 *
 * Design:
 * - Orange/amber accent color (distinct from other nodes)
 * - 400x500 default size, resizable
 * - Floating header with title input (shows on hover/select)
 */

import { memo, useState, useCallback, useRef, useEffect, type ChangeEvent, type KeyboardEvent } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, type Node } from '@xyflow/react';
import type { ChatNodeData, CanvasChatMessage, ChatNodeModelSettings, ChatImageAttachment } from '@deep-agents/shared-types';
import { createDefaultModelSettings } from '@deep-agents/shared-types';
import type * as Y from 'yjs';
import { useChatNode } from '../../hooks/useChatNode';
import { ChatNodeSettingsPanel } from './ChatNodeSettingsPanel';
import { ModelSelectionDialog } from './ModelSelectionDialog';

// ============================================================================
// TYPES
// ============================================================================

type ChatNodeType = Node<ChatNodeData, 'chat'>;

type ChatNodeRuntimeData = ChatNodeData & {
  nodesMap?: Y.Map<Node>;
  edgesMap?: Y.Map<any>;
  ydoc?: Y.Doc;
  makeChange?: (fn: () => void) => void;
  /** Callback when model settings change */
  onModelSettingsChange?: (settings: ChatNodeModelSettings) => void;
};

// ============================================================================
// ICONS
// ============================================================================

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const PinIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22V8M12 8L19 15M12 8L5 15" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const StopIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

const LoadingIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" className="animate-spin">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.25" />
    <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Settings/sliders icon for opening the model settings panel
const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="4" y1="21" x2="4" y2="14" />
    <line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" />
    <line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" />
    <line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </svg>
);

// Image/attachment icon for adding images
const ImageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

// X icon for removing images
const XIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface ChatMessageItemProps {
  message: CanvasChatMessage;
  onPin: (id: string) => void;
  isStreaming?: boolean;
}

function ChatMessageItem({ message, onPin, isStreaming }: ChatMessageItemProps) {
  const isUser = message.role === 'user';
  const isPinned = message.isPinned;
  const hasImages = message.images && message.images.length > 0;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`
          relative max-w-[85%] px-3 py-2 rounded-xl text-sm
          ${isUser
            ? 'bg-amber-500 text-white'
            : 'bg-slate-100 text-slate-700'
          }
        `}
      >
        {/* Display images if present */}
        {hasImages && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {message.images!.map((img) => (
              <img
                key={img.id}
                src={`data:${img.mimeType};base64,${img.base64}`}
                alt={img.filename || 'Attached image'}
                className="w-24 h-24 object-cover rounded-lg border border-white/20 shadow-sm"
              />
            ))}
          </div>
        )}

        {/* Text content */}
        <p className="whitespace-pre-wrap break-words">{message.content || (hasImages ? '' : '\u00A0')}</p>

        {/* Pin button for assistant messages */}
        {!isUser && !isStreaming && message.content && (
          <button
            type="button"
            onClick={() => onPin(message.id)}
            disabled={isPinned}
            className={`
              absolute -right-1 -bottom-1 p-1 rounded-full
              transition-all shadow-sm
              ${isPinned
                ? 'bg-emerald-100 text-emerald-600 cursor-default'
                : 'bg-white text-slate-400 hover:text-amber-600 hover:bg-amber-50'
              }
            `}
            title={isPinned ? 'Pinned to canvas' : 'Pin to canvas'}
          >
            {isPinned ? <CheckIcon /> : <PinIcon />}
          </button>
        )}

        {/* Streaming indicator */}
        {isStreaming && (
          <span className="inline-block ml-1 animate-pulse">...</span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ChatNode = memo(({ id, data, selected, dragging }: NodeProps<ChatNodeType>) => {
  const {
    title = '',
    boardId = '',
    nodesMap,
    edgesMap,
    ydoc,
    makeChange,
    onTitleChange,
    modelSettings: initialModelSettings,
    onModelSettingsChange,
  } = data as ChatNodeRuntimeData;

  // ==================== DYNAMIC SHADOW ====================

  /**
   * Get shadow style based on node state
   * Matches StickyNoteNode, ShapeNode, and BlockNode behavior for consistency
   */
  const getShadowStyle = () => {
    if (dragging) {
      return 'var(--shadow-elevation-high)';
    }
    if (selected) {
      return 'var(--shadow-elevation-medium)';
    }
    return 'var(--shadow-elevation-low)';
  };

  // Local state
  const [localTitle, setLocalTitle] = useState(title);
  const [isHovered, setIsHovered] = useState(false);
  const [inputValue, setInputValue] = useState('');
  // Settings panel visibility state
  const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
  // Model selection dialog visibility state
  const [isModelDialogOpen, setIsModelDialogOpen] = useState(false);
  // Model settings state (use initial or create defaults)
  const [modelSettings, setModelSettings] = useState<ChatNodeModelSettings>(
    () => initialModelSettings ?? createDefaultModelSettings()
  );
  // Pending images to be sent with the next message
  const [pendingImages, setPendingImages] = useState<ChatImageAttachment[]>([]);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use the hook if we have all runtime props
  const hookEnabled = !!(boardId && nodesMap && edgesMap && ydoc && makeChange);

  const {
    messages,
    status,
    error,
    isOutputConnected,
    sendMessage,
    pinMessage,
    clearChat,
    stop,
  } = useChatNode({
    nodeId: id,
    boardId,
    nodesMap: nodesMap!,
    edgesMap: edgesMap!,
    ydoc: ydoc!,
    makeChange: makeChange!,
    // Pass current model settings to the hook
    modelSettings,
  });

  // Sync title with props
  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  // Sync model settings with props when they change externally
  useEffect(() => {
    if (initialModelSettings) {
      setModelSettings(initialModelSettings);
    }
  }, [initialModelSettings]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle model settings change
  const handleModelSettingsChange = useCallback(
    (newSettings: ChatNodeModelSettings) => {
      setModelSettings(newSettings);
      // Persist to node data via callback
      onModelSettingsChange?.(newSettings);
    },
    [onModelSettingsChange]
  );

  // Toggle settings panel
  const toggleSettingsPanel = useCallback(() => {
    setIsSettingsPanelOpen((prev) => !prev);
  }, []);

  // Handle model selection from dialog
  const handleModelSelect = useCallback(
    (modelId: string) => {
      const newSettings = { ...modelSettings, modelId };
      setModelSettings(newSettings);
      onModelSettingsChange?.(newSettings);
    },
    [modelSettings, onModelSettingsChange]
  );

  // Handle title change
  const handleTitleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newTitle = e.target.value;
      setLocalTitle(newTitle);
      onTitleChange?.(newTitle);
    },
    [onTitleChange]
  );

  // Handle send - now includes images
  const handleSend = useCallback(async () => {
    console.log('[ChatNode] handleSend called, hookEnabled:', hookEnabled, 'inputValue:', inputValue);

    // Allow sending if there's text OR images
    const hasContent = inputValue.trim() || pendingImages.length > 0;
    if (!hasContent || !hookEnabled) {
      console.log('[ChatNode] Early return - hasContent:', hasContent, 'hookEnabled:', hookEnabled);
      return;
    }

    const content = inputValue;
    const images = pendingImages.length > 0 ? [...pendingImages] : undefined;

    // Clear input and images before sending
    setInputValue('');
    setPendingImages([]);

    // Send message with images
    await sendMessage(content, images);
  }, [inputValue, pendingImages, sendMessage, hookEnabled]);

  // Handle enter key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Open file picker for image selection
  const handleImageButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle file selection - convert to base64
  const handleFileChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Process each selected file
    const newImages: ChatImageAttachment[] = [];

    for (const file of Array.from(files)) {
      // Only accept images
      if (!file.type.startsWith('image/')) {
        console.warn('[ChatNode] Skipping non-image file:', file.name);
        continue;
      }

      // Limit file size (5MB max to avoid huge base64 strings)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        console.warn('[ChatNode] Skipping large file:', file.name, file.size);
        continue;
      }

      try {
        // Read file as base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            // Extract base64 data (remove "data:image/xxx;base64," prefix)
            const result = reader.result as string;
            const base64Data = result.split(',')[1];
            resolve(base64Data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        newImages.push({
          id: crypto.randomUUID(),
          mimeType: file.type,
          base64,
          filename: file.name,
        });
      } catch (err) {
        console.error('[ChatNode] Error reading file:', err);
      }
    }

    // Add to pending images (limit to 4 images max)
    setPendingImages((prev) => [...prev, ...newImages].slice(0, 4));

    // Reset file input so the same file can be selected again
    e.target.value = '';
  }, []);

  // Remove a pending image
  const removePendingImage = useCallback((imageId: string) => {
    setPendingImages((prev) => prev.filter((img) => img.id !== imageId));
  }, []);

  // Control visibility
  const showControls = isHovered || selected;
  const isStreaming = status === 'streaming';

  // Calculate total width: base card (400) + settings panel (288 = w-72) + gap when open
  const panelWidth = 288; // w-72 = 18rem = 288px
  const gap = 8; // gap between card and panel

  return (
    <div
      className="relative transition-all duration-300 flex items-start"
      style={{
        padding: '0 8px',
        // Expand the node width when settings panel is open
        width: isSettingsPanelOpen ? 400 + panelWidth + gap : 400,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="!w-4 !h-4 !bg-slate-400 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="!w-4 !h-4 !bg-amber-500 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
      />

      {/* Floating Header */}
      <div
        className={`
          absolute bottom-full left-0 right-0 pb-2
          transition-opacity duration-200 z-30
          ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        <div className="flex items-center gap-2 px-1">
          <input
            type="text"
            value={localTitle}
            onChange={handleTitleChange}
            placeholder="Chat"
            className="nodrag nopan px-2.5 py-1 text-[12px] font-semibold text-amber-700
              bg-gradient-to-r from-amber-50 to-orange-50
              rounded-lg border border-amber-100/50
              outline-none focus:border-amber-300 focus:ring-1 focus:ring-amber-200
              placeholder:text-amber-300 placeholder:font-medium
              min-w-[80px] max-w-[200px] truncate transition-all"
            style={{ width: `${Math.max(80, Math.min(200, (localTitle.length || 4) * 8))}px` }}
          />
          <button
            type="button"
            onClick={clearChat}
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            title="Clear chat"
          >
            <TrashIcon />
          </button>
        </div>
      </div>

      {/* Main Card Container */}
      <div
        className="
          relative bg-white rounded-2xl overflow-hidden
          border transition-all duration-300
          border-neutral-200/60
        "
        style={{
          // Dynamic shadow based on drag/select state (matches all other nodes)
          boxShadow: getShadowStyle(),
          // Smooth shadow transition + scale effect
          transition: dragging
            ? 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'box-shadow 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          // Slight scale up when dragging
          transform: dragging ? 'scale(1.01)' : 'scale(1)',
          width: 400,
          height: 500,
        }}
      >
        {/* Resize Handles */}
        <NodeResizer
          minWidth={320}
          minHeight={350}
          isVisible={selected}
          lineClassName="border-amber-500"
          handleClassName="bg-amber-500 border-2 border-white rounded"
        />

        {/* Content Area */}
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <span className="text-amber-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </span>
              <span className="text-sm font-medium text-slate-700">Chat</span>
              {isStreaming && (
                <span className="text-amber-500">
                  <LoadingIcon />
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {/* Settings button - always visible */}
              <button
                type="button"
                onClick={toggleSettingsPanel}
                className={`
                  p-1.5 rounded-lg transition-colors
                  ${isSettingsPanelOpen
                    ? 'text-amber-600 bg-amber-50'
                    : 'text-slate-400 hover:text-amber-600 hover:bg-amber-50'
                  }
                `}
                title="Model settings"
              >
                <SettingsIcon />
              </button>
              {/* Stop button - only when streaming */}
              {isStreaming && (
                <button
                  type="button"
                  onClick={stop}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  title="Stop generating"
                >
                  <StopIcon />
                </button>
              )}
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 px-4 py-3 overflow-y-auto space-y-3">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="text-amber-200 mb-2">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <p className="text-sm text-slate-400">Start a conversation</p>
                <p className="text-xs text-slate-300 mt-1">Messages sync with collaborators</p>
              </div>
            ) : (
              <>
                {messages.map((msg, index) => (
                  <ChatMessageItem
                    key={msg.id}
                    message={msg}
                    onPin={pinMessage}
                    isStreaming={isStreaming && index === messages.length - 1 && msg.role === 'assistant'}
                  />
                ))}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-slate-100">
            {/* Hidden file input for image selection */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />

            {error && (
              <div className="mb-2 px-3 py-2 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            {/* Image preview area - shows pending images */}
            {pendingImages.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {pendingImages.map((img) => (
                  <div key={img.id} className="relative group">
                    <img
                      src={`data:${img.mimeType};base64,${img.base64}`}
                      alt={img.filename || 'Pending image'}
                      className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                    />
                    {/* Remove button */}
                    <button
                      type="button"
                      onClick={() => removePendingImage(img.id)}
                      className="nodrag nopan absolute -top-1 -right-1 w-5 h-5
                        bg-red-500 text-white rounded-full
                        flex items-center justify-center
                        opacity-0 group-hover:opacity-100 transition-opacity
                        hover:bg-red-600"
                      title="Remove image"
                    >
                      <XIcon />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2">
              {/* Image upload button */}
              <button
                type="button"
                onClick={handleImageButtonClick}
                disabled={!hookEnabled || isStreaming || pendingImages.length >= 4}
                className="nodrag nopan p-2 rounded-lg border
                  bg-amber-50 border-amber-200 text-amber-500
                  hover:bg-amber-100 hover:text-amber-600 hover:border-amber-300
                  transition-colors
                  disabled:opacity-40 disabled:cursor-not-allowed"
                title={pendingImages.length >= 4 ? 'Max 4 images' : 'Add image'}
              >
                <ImageIcon />
              </button>

              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                disabled={!hookEnabled || isStreaming}
                className="nodrag nopan flex-1 px-3 py-2 text-sm text-slate-700
                  bg-slate-50 rounded-lg border border-slate-200
                  outline-none focus:border-amber-300 focus:ring-1 focus:ring-amber-200
                  placeholder:text-slate-400
                  disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!hookEnabled || isStreaming || (!inputValue.trim() && pendingImages.length === 0)}
                className="nodrag nopan px-4 py-2 rounded-lg
                  bg-amber-500 text-white text-sm font-medium
                  hover:bg-amber-600 transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center gap-2"
              >
                {isStreaming ? <LoadingIcon /> : <SendIcon />}
              </button>
            </div>

            {isOutputConnected && (
              <p className="mt-2 text-[10px] text-amber-600">
                Response will also stream to connected block
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Settings Side Panel - expands to the right, sticky positioning */}
      <ChatNodeSettingsPanel
        settings={modelSettings}
        onSettingsChange={handleModelSettingsChange}
        isOpen={isSettingsPanelOpen}
        onClose={() => setIsSettingsPanelOpen(false)}
        onOpenModelSelect={() => setIsModelDialogOpen(true)}
      />

      {/* Model Selection Dialog - portal outside of node */}
      <ModelSelectionDialog
        isOpen={isModelDialogOpen}
        onClose={() => setIsModelDialogOpen(false)}
        onSelect={handleModelSelect}
        selectedModelId={modelSettings.modelId}
      />

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
});

ChatNode.displayName = 'ChatNode';
